// The signed-in user's pins and private chat names, live (owner, 2026-10-21).
//
// One query for the whole panel (`['chat-prefs']`). Optimistic: a tap pins at
// once, and a refusal puts it back with a message. It survives a reload (it is
// the server's), and the user's other devices pick it up on focus and every
// minute. The query cache is wiped on sign-out (lib/queryClient.ts), and the
// worker only ever answers with the caller's own rows — another user on the
// same device never sees them.
//
// `installed: false` (migration 260 not applied) or no answer at all = the
// controls are not shown; the list is exactly what it was.
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatPrefsApi, type ChatPref, type ChatPrefSave } from '@/lib/chatPrefsApi';
import type { WhatsAppConversation } from '@/lib/api';
import { planSave, prefFor, type PrefChange } from './chatPrefs';

export const CHAT_PREFS_KEY = ['chat-prefs'] as const;
type Data = { installed: boolean; prefs: ChatPref[] };

export interface ChatPrefsHandle {
  /** The controls are offered (the server has the table). */
  ready: boolean;
  prefs: ChatPref[];
  prefOf: (chatId: string, phone?: string | null) => ChatPref | null;
  save: (chatId: string, phone: string | null | undefined, change: PrefChange) => void;
  error: string | null;
  clearError: () => void;
}

export function useChatPrefs(): ChatPrefsHandle {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: CHAT_PREFS_KEY,
    queryFn: () => chatPrefsApi.list(),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  const prefs = data?.prefs ?? [];
  const ready = data?.installed === true;

  const mut = useMutation({
    mutationFn: (v: { body: ChatPrefSave; next: ChatPref[] }) => chatPrefsApi.save(v.body),
    onMutate: async (v) => {
      setError(null);
      await qc.cancelQueries({ queryKey: CHAT_PREFS_KEY });
      const before = qc.getQueryData<Data>(CHAT_PREFS_KEY);
      qc.setQueryData<Data>(CHAT_PREFS_KEY, { installed: true, prefs: v.next });
      return { before };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.before) qc.setQueryData(CHAT_PREFS_KEY, ctx.before);
      setError((e as Error)?.message || 'error');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CHAT_PREFS_KEY }),
  });

  // No number given (the Inbox knows only the chat id): the one the
  // Conversations list already resolved, so a pin keyed on the phone JID is
  // still found from a @lid id.
  const prefOf = useCallback((chatId: string, phone?: string | null) => {
    const known = phone !== undefined ? phone
      : qc.getQueryData<{ conversations: WhatsAppConversation[] }>(['whatsapp-conversations'])
        ?.conversations?.find((c) => c.chat_id === chatId)?.phone ?? null;
    return prefFor(prefs, chatId, known);
  }, [prefs, qc]);
  const save = useCallback((chatId: string, phone: string | null | undefined, change: PrefChange) => {
    const current = qc.getQueryData<Data>(CHAT_PREFS_KEY)?.prefs ?? [];
    mut.mutate(planSave(current, chatId, phone, change));
  }, [qc, mut]);

  return { ready, prefs, prefOf, save, error, clearError: () => setError(null) };
}
